import React, {useCallback, useState} from 'react';

import {NoResultPage} from 'src/components/no-result-page/no-result-page';
import {SsoAuditRecord} from 'src/openapi/admin/models/SsoAuditRecord';
import {formatTimestamp} from 'src/utils/date-utils';

import {BoxLoading} from '@pb/kit-component-box-loading';
import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {Message} from '@pb/kit-component-message';
import {Body, Cell, Row, Table} from '@pb/kit-component-table';

import {useAuditLog} from '../../hooks/use-audit-log';
import {useSupportedSsoActions} from '../../hooks/use-supported-sso-actions';
import {formatInitiator} from '../../utils/format-initiator';

import {AuditLogDrawer} from './audit-log-drawer';
import {AuditLogTableHeader} from './audit-log-table-header';

import {actionLink} from './existing-connection.module.styl';


export const AuditLogSection: React.FC = () => {
    const {
        entries,
        loading,
        error,
        actionType,
        onActionTypeChange,
    } = useAuditLog();
    const {actionOptions, getTitleById} = useSupportedSsoActions();
    const [selectedRecord, setSelectedRecord] = useState<SsoAuditRecord | null>(null);

    const handleCloseDrawer = useCallback(() => {
        setSelectedRecord(null);
    }, []);

    return (
        <div>
            {error != null && (
                <Message color="error">{error}</Message>
            )}

            <BoxLoading loading={loading}>
                {entries.length === 0 ? (
                    <NoResultPage
                        title="No data to show."
                        description=""
                    />
                ) : (
                    <Table borderedColumns={false}>
                        <AuditLogTableHeader
                            actionType={actionType}
                            onActionTypeChange={onActionTypeChange}
                            actionOptions={actionOptions}
                        />
                        <Body>
                            {entries.map((entry, index) => (
                                <Row key={entry.recordId ?? `${entry.timestamp}-${index}`}>
                                    <Cell>{index + 1}</Cell>
                                    <Cell>
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            className={actionLink}
                                            onClick={() => setSelectedRecord(entry)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    setSelectedRecord(entry);
                                                }
                                            }}>
                                            <EllipsisTextHint
                                                title={getTitleById(entry.actionName) ?? entry.actionName}>
                                                {getTitleById(entry.actionName) ?? entry.actionName}
                                            </EllipsisTextHint>
                                        </span>
                                    </Cell>
                                    <Cell>{entry.actionDescription}</Cell>
                                    <Cell>
                                        <EllipsisTextHint title={formatInitiator(entry)}>
                                            {formatInitiator(entry)}
                                        </EllipsisTextHint>
                                    </Cell>
                                    <Cell>{formatTimestamp(entry.timestamp)}</Cell>
                                    <Cell>
                                        <EllipsisTextHint title={entry.reason ?? '—'}>
                                            {entry.reason ?? '—'}
                                        </EllipsisTextHint>
                                    </Cell>
                                </Row>
                            ))}
                        </Body>
                    </Table>
                )}
            </BoxLoading>

            <AuditLogDrawer
                record={selectedRecord}
                onClose={handleCloseDrawer}
                getTitleById={getTitleById}
            />
        </div>
    );
};
